import {
    ButtonGhost,
    Colors,
    DraggableMultiContainerList,
    Icon,
    ModalDialog,
    Text,
} from '@amelio/ui-library';
import { useCallback, useState } from 'react';

import { RankingContainer } from './components/RankingContainer';
import { RankingItem } from './components/RankingItem';

import type {
    DraggableContainer,
    DraggableContainerMetadata,
    DraggableMultiContainerItem,
    DraggableMultiContainerItemMetadata,
    DragStartEvent,
} from '@amelio/ui-library';

import dragDropEn from '@/assets/media/drag-drop-en.gif';
import dragDropFr from '@/assets/media/drag-drop-fr.gif';
import { useAmelioTranslations } from '@/hooks';

export interface RankingQuestionOption {
    id: string;
    label: string;
}

export interface RankingQuestionAnswerProps {
    /** Question text to display at the top */
    question?: string;
    /** All available options to rank */
    options: RankingQuestionOption[];
    /** Currently ranked option IDs in order */
    rankedOptionIds?: string[];
    /** Callback when ranking changes */
    onRankingChange: (rankedIds: string[]) => void;
    /** Show error state */
    hasError?: boolean;
    /** Disable interaction */
    disabled?: boolean;
    /** Optional class override */
    className?: string;
    /** Test id */
    dataTestId?: string;
}

const NEXT_CONTAINER_ID = 'next-zone';
const RANKING_CONTAINER_ID = 'ranking-zone';
const FRENCH_LANGUAGE_PREFIX = 'fr';

// Intrinsic pixel sizes of each animation, so the image reserves its box and keeps its ratio.
const HELP_ANIMATIONS = {
    fr: { src: dragDropFr, width: 800, height: 778 },
    en: { src: dragDropEn, width: 800, height: 761 },
} as const;

/**
 * RankingQuestionAnswer
 *
 * Survey answer component for ranking questions.
 * Uses DraggableMultiContainerList from UI Library with two containers:
 * - Next to rank: Shows the next unranked option
 * - Your ranking: Shows ranked options with numbers
 */
const RankingQuestionAnswer = ({
    question,
    options,
    rankedOptionIds = [],
    onRankingChange,
    hasError = false,
    disabled = false,
    className = '',
    dataTestId = 'ranking-question-answer',
}: RankingQuestionAnswerProps) => {
    const { t, i18n } = useAmelioTranslations();

    const helpAnimation = i18n.language?.toLowerCase().startsWith(FRENCH_LANGUAGE_PREFIX)
        ? HELP_ANIMATIONS.fr
        : HELP_ANIMATIONS.en;

    const [isHelpModalOpen, setIsHelpModalOpen] = useState<boolean>(false);

    const handleOpenHelpModal = () => setIsHelpModalOpen(true);
    const handleCloseHelpModal = () => setIsHelpModalOpen(false);

    const [containers, setContainers] = useState<DraggableContainer<{ label: string }>[]>(() => {
        // If no ranked options provided, start with first option in ranking
        const getInitialRankedIds = (): string[] => {
            if (rankedOptionIds.length > 0) {
                return rankedOptionIds;
            }

            if (options.length > 0) {
                return [options[0].id];
            }

            return [];
        };

        const initialRankedIds = getInitialRankedIds();

        const rankedItems = initialRankedIds
            .map((id) => {
                const matchingOption = options.find((option) => option.id === id);
                if (!matchingOption) {
                    return null;
                }
                return { id: matchingOption.id, data: { label: matchingOption.label } };
            })
            .filter(
                (item): item is DraggableMultiContainerItem<{ label: string }> => item !== null,
            );

        // Next unranked is the second option (index 1) if starting fresh, or first unranked if continuing
        const nextUnranked = options.find((option) => !initialRankedIds.includes(option.id));

        return [
            {
                id: NEXT_CONTAINER_ID,
                items: nextUnranked
                    ? [{ id: nextUnranked.id, data: { label: nextUnranked.label } }]
                    : [],
            },
            {
                id: RANKING_CONTAINER_ID,
                items: rankedItems,
            },
        ];
    });

    const handleContainersChange = (newContainers: DraggableContainer<{ label: string }>[]) => {
        const rankingContainer = newContainers.find(
            (container) => container.id === RANKING_CONTAINER_ID,
        );
        const nextContainer = newContainers.find((container) => container.id === NEXT_CONTAINER_ID);
        const currentRankingContainer = containers.find(
            (container) => container.id === RANKING_CONTAINER_ID,
        );
        const currentNextContainer = containers.find(
            (container) => container.id === NEXT_CONTAINER_ID,
        );

        if (
            !rankingContainer ||
            !nextContainer ||
            !currentRankingContainer ||
            !currentNextContainer
        ) {
            return;
        }

        // Block: Prevent items from moving FROM ranking back TO next container (one-way only)
        const itemMovedFromRankingToNext = nextContainer.items.some((item) =>
            currentRankingContainer.items.some((currentItem) => currentItem.id === item.id),
        );

        if (itemMovedFromRankingToNext) {
            // Reject the change - restore previous state
            return;
        }

        // Block: NextElement container can only have 1 item max
        if (nextContainer.items.length > 1) {
            // Reject the change - restore previous state
            return;
        }

        // Detect if an item was moved TO ranking
        const itemMovedToRanking = rankingContainer.items.some(
            (item) =>
                !currentRankingContainer.items.some((currentItem) => currentItem.id === item.id),
        );

        // Auto-populate next item when current is moved to ranking
        if (itemMovedToRanking && nextContainer.items.length === 0) {
            const currentRankedIds = rankingContainer.items.map((item) => item.id);
            const nextUnrankedOption = options.find(
                (option) => !currentRankedIds.includes(option.id),
            );

            const updatedContainers = newContainers.map((container) => {
                if (container.id !== NEXT_CONTAINER_ID) {
                    return container;
                }

                return {
                    ...container,
                    items: nextUnrankedOption
                        ? [{ id: nextUnrankedOption.id, data: { label: nextUnrankedOption.label } }]
                        : [],
                };
            });
            setContainers(updatedContainers);
            onRankingChange(rankingContainer.items.map((item) => item.id));
        } else {
            setContainers(newContainers);
            onRankingChange(rankingContainer.items.map((item) => item.id));
        }
    };

    const rankingContainer = containers.find((container) => container.id === RANKING_CONTAINER_ID);
    const allItemsRanked = rankingContainer?.items.length === options.length;

    const renderItem = useCallback(
        (
            item: DraggableMultiContainerItem<{ label: string }>,
            metadata: DraggableMultiContainerItemMetadata,
        ) => (
            <RankingItem
                item={item}
                metadata={metadata}
                hasError={hasError}
                isNextContainer={metadata.containerId === NEXT_CONTAINER_ID}
                isRankingContainer={metadata.containerId === RANKING_CONTAINER_ID}
            />
        ),
        [hasError],
    );

    const handleDragStart = (
        event: DragStartEvent<{ label: string }>,
    ): DraggableContainer<{ label: string }>[] | void => {
        if (event.sourceContainerId !== NEXT_CONTAINER_ID) {
            return;
        }

        const currentRankedIds = rankingContainer?.items.map((item) => item.id) ?? [];
        const nextUnranked = options.find(
            (option) => option.id !== event.itemId && !currentRankedIds.includes(option.id),
        );

        if (!nextUnranked) {
            return;
        }

        return containers.map((container) => {
            if (container.id !== NEXT_CONTAINER_ID) {
                return container;
            }

            return {
                ...container,
                items: [
                    ...container.items,
                    { id: nextUnranked.id, data: { label: nextUnranked.label } },
                ],
            };
        });
    };

    const renderContainer = useCallback(
        (
            container: DraggableContainer<{ label: string }>,
            metadata: DraggableContainerMetadata,
            children: React.ReactNode,
        ) => (
            <RankingContainer
                container={container}
                metadata={metadata}
                hasError={hasError}
                isNextContainer={container.id === NEXT_CONTAINER_ID}
                isRankingContainer={container.id === RANKING_CONTAINER_ID}
                allItemsRanked={allItemsRanked}
                totalOptions={options.length}
            >
                {children}
            </RankingContainer>
        ),
        [hasError, allItemsRanked, options.length],
    );

    const canDrop = useCallback((sourceContainerId: string, destinationContainerId: string) => {
        if (
            sourceContainerId === RANKING_CONTAINER_ID &&
            destinationContainerId === NEXT_CONTAINER_ID
        ) {
            return false;
        }
        return true;
    }, []);

    return (
        <div
            className={`w-full ${className}`}
            role={'group'}
            aria-label={'Ranking question'}
            aria-disabled={disabled}
            data-testid={dataTestId}
        >
            {question && (
                <Text
                    weight={'semibold'}
                    size={'large'}
                    color={Colors.WHITE}
                    className={'mb-4'}
                >
                    {question}
                </Text>
            )}

            <DraggableMultiContainerList
                containers={containers}
                onChange={handleContainersChange}
                onDragStart={handleDragStart}
                renderItem={renderItem}
                renderContainer={renderContainer}
                disabled={disabled}
                dataTestId={dataTestId}
                containerGap={20}
                itemGap={12}
                canDrop={canDrop}
            />

            <ButtonGhost
                onClick={handleOpenHelpModal}
                disabled={disabled}
                dataTestId={`${dataTestId}-help-button`}
                leftIcon={
                    <Icon
                        name={'support-message-question'}
                        size={'md'}
                        color={Colors.WHITE}
                    />
                }
            >
                <Text
                    size={'small'}
                    color={Colors.WHITE}
                    className={'underline'}
                >
                    {t('evaluation360Questionnaire.rankingQuestion.helpText')}
                </Text>
            </ButtonGhost>

            <ModalDialog
                isOpen={isHelpModalOpen}
                onClose={handleCloseHelpModal}
                title={t('evaluation360Questionnaire.rankingQuestion.helpModalTitle')}
                size={'md'}
                closeAriaLabel={t('common.close')}
                backdropBlur
                dataTestId={`${dataTestId}-help-modal`}
            >
                <div className={'flex flex-col gap-4'}>
                    <Text size={'default'}>
                        {t('evaluation360Questionnaire.rankingQuestion.helpModalText')}
                    </Text>

                    <img
                        src={helpAnimation.src}
                        width={helpAnimation.width}
                        height={helpAnimation.height}
                        alt={t('evaluation360Questionnaire.rankingQuestion.helpModalImageAlt')}
                        className={'h-auto w-full rounded'}
                        data-testid={`${dataTestId}-help-modal-image`}
                    />
                </div>
            </ModalDialog>
        </div>
    );
};

export { RankingQuestionAnswer };
